import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
    BarChart3, 
    Users, 
    DollarSign, 
    TrendingUp,
    Globe,
    Mail,
    Calendar,
    ArrowLeft,
    RefreshCw,
    CheckCircle,
    Clock,
    CreditCard
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color = "magenta" }) => {
    const colorClasses = {
        magenta: "text-magenta bg-magenta/10",
        emerald: "text-emerald-400 bg-emerald-500/10",
        blue: "text-blue-400 bg-blue-500/10",
        amber: "text-amber-400 bg-amber-500/10"
    };

    return (
        <Card className="glass-card border-white/10">
            <CardContent className="p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-gray-400 text-sm mb-1">{title}</p>
                        <p className="text-3xl font-bold text-white font-heading">{value}</p>
                        {subtitle && <p className="text-gray-500 text-xs mt-1">{subtitle}</p>}
                    </div>
                    <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
                        <Icon className="h-6 w-6" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

function AdminDashboard({ onBack }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API}/admin/stats`);
            setStats(response.data);
        } catch (err) {
            console.error("Failed to fetch stats:", err);
            setError("Failed to load dashboard data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-obsidian flex items-center justify-center">
                <div className="text-center">
                    <div className="loading-spinner mx-auto mb-4" />
                    <p className="text-gray-400">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-obsidian flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-400 mb-4">{error}</p>
                    <Button onClick={fetchStats} variant="outline">
                        <RefreshCw className="mr-2 h-4 w-4" /> Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-obsidian">
            <div className="noise-overlay" />
            
            {/* Header */}
            <header className="glass-card border-b border-white/5 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={onBack}
                            className="text-gray-400 hover:text-white"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                        <img src={LOGO_URL} alt="SiteGradePro" className="h-10 w-auto object-contain" />
                        <div>
                            <h1 className="font-heading font-bold text-white text-lg">Admin Dashboard</h1>
                            <p className="text-xs text-gray-500">SiteGradePro Analytics</p>
                        </div>
                    </div>
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={fetchStats}
                        className="border-gray-700 text-gray-400 hover:text-white"
                    >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <StatCard 
                        title="Total Analyses" 
                        value={stats.total_analyses}
                        subtitle={`${stats.analyses_today} today`}
                        icon={BarChart3}
                        color="magenta"
                    />
                    <StatCard 
                        title="Total Leads" 
                        value={stats.total_leads}
                        subtitle={`${stats.leads_today} today`}
                        icon={Users}
                        color="blue"
                    />
                    <StatCard 
                        title="Total Revenue" 
                        value={formatCurrency(stats.total_revenue)}
                        subtitle={`${stats.successful_payments} paid subscriptions`}
                        icon={DollarSign}
                        color="emerald"
                    />
                    <StatCard 
                        title="Conversion Rate" 
                        value={stats.total_leads > 0 ? `${((stats.successful_payments / stats.total_leads) * 100).toFixed(1)}%` : "0%"}
                        subtitle="Leads → Paid"
                        icon={TrendingUp}
                        color="amber"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Leads */}
                    <Card className="glass-card border-white/10">
                        <CardHeader>
                            <CardTitle className="text-white font-heading flex items-center gap-2">
                                <Mail className="h-5 w-5 text-blue-400" />
                                Recent Leads
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {stats.recent_leads.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No leads yet</p>
                            ) : (
                                <div className="space-y-3">
                                    {stats.recent_leads.map((lead, i) => (
                                        <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-white font-medium truncate">
                                                    {lead.name || lead.email}
                                                </p>
                                                <p className="text-gray-500 text-sm truncate">{lead.email}</p>
                                            </div>
                                            <div className="text-right ml-4">
                                                <p className="text-gray-400 text-xs flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" />
                                                    {formatDate(lead.created_at)}
                                                </p>
                                                {lead.website && (
                                                    <p className="text-gray-500 text-xs flex items-center gap-1 mt-1">
                                                        <Globe className="h-3 w-3" />
                                                        {lead.website.replace(/^https?:\/\//, '').slice(0, 20)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Recent Payments */}
                    <Card className="glass-card border-white/10">
                        <CardHeader>
                            <CardTitle className="text-white font-heading flex items-center gap-2">
                                <CreditCard className="h-5 w-5 text-emerald-400" />
                                Recent Payments
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {stats.recent_payments.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No payments yet</p>
                            ) : (
                                <div className="space-y-3">
                                    {stats.recent_payments.map((payment, i) => (
                                        <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-white font-medium">
                                                    {payment.plan_name || "Unknown Plan"}
                                                </p>
                                                <p className="text-gray-500 text-sm truncate">{payment.email || "No email"}</p>
                                            </div>
                                            <div className="text-right ml-4">
                                                <p className={`font-bold ${payment.payment_status === 'paid' ? 'text-emerald-400' : 'text-amber-400'}`}>
                                                    {formatCurrency(payment.amount)}
                                                </p>
                                                <p className="text-xs flex items-center justify-end gap-1 mt-1">
                                                    {payment.payment_status === 'paid' ? (
                                                        <><CheckCircle className="h-3 w-3 text-emerald-400" /><span className="text-emerald-400">Paid</span></>
                                                    ) : (
                                                        <><Clock className="h-3 w-3 text-amber-400" /><span className="text-amber-400">Pending</span></>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Top Domains */}
                    <Card className="glass-card border-white/10 lg:col-span-2">
                        <CardHeader>
                            <CardTitle className="text-white font-heading flex items-center gap-2">
                                <Globe className="h-5 w-5 text-magenta" />
                                Top Analyzed Domains
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {stats.top_domains.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No analyses yet</p>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {stats.top_domains.map((domain, i) => (
                                        <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="text-gray-500 font-mono text-sm">#{i + 1}</span>
                                                <span className="text-white truncate">{domain.domain?.replace(/^https?:\/\//, '')}</span>
                                            </div>
                                            <span className="text-magenta font-bold ml-2">{domain.count}x</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </main>
        </div>
    );
}

export default AdminDashboard;
